import { useEffect, useState } from "react";
import api from "../services/api";
import { IndianRupee, Check, X, Loader2, Clock, ArrowDownToLine, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("payouts");
  const [refundForm, setRefundForm] = useState({ tracking_id: "", reason: "", amount: "" });
  const [showRefund, setShowRefund] = useState(false);

  const fetchData = async () => {
    try {
      const [pRes, sRes, rRes] = await Promise.all([
        api.get("/admin/payouts" + (filter ? `?status=${filter}` : "")),
        api.get("/admin/payouts/summary"),
        api.get("/admin/refunds"),
      ]);
      setPayouts(pRes.data);
      setSummary(sRes.data);
      setRefunds(rRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handlePayout = async (payoutId, action, notes) => {
    setProcessing(payoutId);
    try {
      await api.put(`/admin/payouts/${payoutId}`, { action, notes });
      toast.success(`Payout ${action}d`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setProcessing(null);
  };

  const handleRefund = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/refunds", {
        tracking_id: refundForm.tracking_id,
        reason: refundForm.reason,
        amount: refundForm.amount ? parseFloat(refundForm.amount) : null,
      });
      toast.success("Refund processed");
      setShowRefund(false);
      setRefundForm({ tracking_id: "", reason: "", amount: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      processed: "bg-blue-50 text-blue-700 border-blue-200",
    };
    const labels = { pending: "Pending", approved: "Paid", paid: "Paid", rejected: "Rejected", processed: "Processed" };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg border text-[11px] font-semibold ${map[status] || "bg-slate-50 text-slate-500"}`} data-testid={`status-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Payouts & Refunds</h1>
          <p className="text-sm text-slate-500 mt-1">Manage driver payouts and customer refunds</p>
        </div>
        <Button onClick={() => setShowRefund(!showRefund)} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl gap-2" data-testid="create-refund-btn">
          <ArrowDownToLine className="h-4 w-4" /> Process Refund
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="payout-summary">
          <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Pending</span>
            </div>
            <p className="font-['Outfit'] text-2xl font-bold text-slate-900">INR {summary.pending.total}</p>
            <p className="text-xs text-slate-400 mt-0.5">{summary.pending.count} requests</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Check className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Paid</span>
            </div>
            <p className="font-['Outfit'] text-2xl font-bold text-slate-900">INR {summary.approved.total}</p>
            <p className="text-xs text-slate-400 mt-0.5">{summary.approved.count} payouts</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-5">
            <div className="flex items-center gap-2 mb-2">
              <X className="h-4 w-4 text-red-600" />
              <span className="text-xs font-bold text-red-600 uppercase tracking-wider">Rejected</span>
            </div>
            <p className="font-['Outfit'] text-2xl font-bold text-slate-900">INR {summary.rejected.total}</p>
            <p className="text-xs text-slate-400 mt-0.5">{summary.rejected.count} requests</p>
          </div>
        </div>
      )}

      {/* Refund Form */}
      {showRefund && (
        <form onSubmit={handleRefund} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 animate-fadeInUp" data-testid="refund-form">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Process Refund</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input placeholder="Tracking ID (PKG-XXX)" value={refundForm.tracking_id} onChange={(e) => setRefundForm({ ...refundForm, tracking_id: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" required data-testid="refund-tracking-input" />
            <input placeholder="Reason" value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" required data-testid="refund-reason-input" />
            <input placeholder="Amount (blank = full)" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" type="number" data-testid="refund-amount-input" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button type="submit" className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl" data-testid="submit-refund-btn">Process Refund</Button>
            <Button type="button" variant="outline" onClick={() => setShowRefund(false)} className="rounded-xl">Cancel</Button>
          </div>
        </form>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-3">
        <button onClick={() => setTab("payouts")} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "payouts" ? "bg-[#FF6B00] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-payouts">
          Driver Payouts
        </button>
        <button onClick={() => setTab("refunds")} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "refunds" ? "bg-[#FF6B00] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-refunds">
          Refunds ({refunds.length})
        </button>
        {tab === "payouts" && (
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ml-auto border border-slate-200 rounded-lg px-3 py-1.5 text-xs" data-testid="payout-filter">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
      </div>

      {/* Payouts Table */}
      {tab === "payouts" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="payouts-table">
          {payouts.length === 0 ? (
            <div className="p-12 text-center">
              <IndianRupee className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base font-semibold text-slate-700">No payout requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 bg-slate-50/60">
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-5">Agent</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Amount</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Withdrawal Details</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Requested</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((p) => (
                    <TableRow key={p.payout_id} className="border-slate-50">
                      <TableCell className="pl-5">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{p.agent_name}</p>
                          <p className="text-xs text-slate-400">{p.agent_email}</p>
                          {p.agent_phone && <p className="text-xs text-slate-400">{p.agent_phone}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-bold text-slate-900 flex items-center gap-0.5">
                          <IndianRupee className="h-3.5 w-3.5" />{p.amount}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          {p.withdrawal_method === "upi" ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">UPI</span>
                              <p className="text-xs text-slate-700 font-medium mt-0.5">{p.withdrawal_details?.upi_id || "—"}</p>
                            </div>
                          ) : (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">Bank</span>
                              <p className="text-xs text-slate-700 font-medium mt-0.5">{p.withdrawal_details?.bank_name || "—"}</p>
                              <p className="text-[10px] text-slate-400">A/C: {p.withdrawal_details?.bank_account || "—"}</p>
                              <p className="text-[10px] text-slate-400">IFSC: {p.withdrawal_details?.bank_ifsc || "—"}</p>
                              <p className="text-[10px] text-slate-400">Holder: {p.withdrawal_details?.account_holder || "—"}</p>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{statusBadge(p.status)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(p.requested_at).toLocaleString()}</TableCell>
                      <TableCell>
                        {p.status === "pending" ? (
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => handlePayout(p.payout_id, "approve")} disabled={processing === p.payout_id} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-8 text-xs gap-1" data-testid={`approve-${p.payout_id}`}>
                              {processing === p.payout_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Mark as Paid
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handlePayout(p.payout_id, "reject", "Rejected by admin")} disabled={processing === p.payout_id} className="rounded-lg h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 gap-1" data-testid={`reject-${p.payout_id}`}>
                              <X className="h-3 w-3" /> Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">{p.processed_at ? new Date(p.processed_at).toLocaleDateString() : "—"}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Refunds Table */}
      {tab === "refunds" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="refunds-table">
          {refunds.length === 0 ? (
            <div className="p-12 text-center">
              <ArrowDownToLine className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base font-semibold text-slate-700">No refunds processed</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 bg-slate-50/60">
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-5">Refund ID</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Amount</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Reason</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {refunds.map((r) => (
                    <TableRow key={r.refund_id} className="border-slate-50">
                      <TableCell className="pl-5 text-sm font-mono font-semibold text-slate-900">{r.refund_id}</TableCell>
                      <TableCell className="text-sm font-mono text-slate-700">{r.tracking_id}</TableCell>
                      <TableCell>
                        <span className="text-sm font-bold text-red-600 flex items-center gap-0.5">
                          <IndianRupee className="h-3.5 w-3.5" />{r.amount}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{r.reason}</TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
